import { useState, useCallback } from 'react';

import { SelectOption } from '~/shared/utils/types';
import { getApiFilters, splitAccountFilters } from '~/shared/utils/apiFilters';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { appendAccountsScope } from '~/shared/utils/organizationUtils';
import { useExportState } from '~/shared/context/ExportStateContext';
import ExportModal, { ExportSection } from '~/shared/components/ExportModal/ExportModal';
import {
  FILTER_OPTIONS,
  FORMAT_OPTIONS,
  type ExportFormat,
  type FilterSelection,
} from '~/shared/components/ExportModal/ExportSections';
import RadioOptionList from '~/shared/components/RadioOptionList';
import { CheckboxOption, SelectableOptionCard } from '~/shared/components/SelectableOption';

const VOLUMES_BASE_URL = '/api/v1/volumes';

interface VolumesExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeFilters: SelectOption[];
  sortField: { field: string; dir: 'desc' | 'asc' };
  totalCount: number | undefined;
}

export default function VolumesExportModal({
  isOpen,
  onClose,
  activeFilters,
  sortField,
  totalCount,
}: VolumesExportModalProps) {
  const { triggerVolumesExport, isExporting } = useExportState();
  const { accounts, activeTenantId } = useAuthContext();

  const [formats, setFormats] = useState<ExportFormat[]>(['csv']);
  const [filterSelection, setFilterSelection] = useState<FilterSelection>('with_filters');

  const toggleFormat = useCallback((format: ExportFormat) => {
    setFormats((prev) =>
      prev.includes(format) ? prev.filter((value) => value !== format) : [...prev, format]
    );
  }, []);

  const buildFilterParams = useCallback(
    (filters: SelectOption[]): string[] => {
      if (filters.length === 0) return [];
      const result = getApiFilters(filters);
      return Array.isArray(result) ? result : [result];
    },
    []
  );

  const handleExport = useCallback(() => {
    const withFilters = filterSelection === 'with_filters';
    const selectedFilters = withFilters ? activeFilters : [];
    const { accountIds, filtersWithoutAccount } = splitAccountFilters(selectedFilters);
    const filterParams = buildFilterParams(filtersWithoutAccount);

    const countQs = new URLSearchParams(filterParams.map((f) => ['filters', f] as [string, string]));
    appendAccountsScope(countQs, accounts, activeTenantId, accountIds);
    const countUrl = `${VOLUMES_BASE_URL}/count?${countQs.toString()}`;

    const dataQs = new URLSearchParams([
      [
        'sortBy',
        JSON.stringify({
          field: sortField?.field || 'volumeId',
          ascending: sortField?.dir !== 'desc',
        }),
      ],
      ...filterParams.map((f) => ['filters', f] as [string, string]),
    ]);
    appendAccountsScope(dataQs, accounts, activeTenantId, accountIds);
    const dataUrl = `${VOLUMES_BASE_URL}?${dataQs.toString()}`;

    onClose();
    triggerVolumesExport({
      countUrl,
      dataUrl,
      formats,
      filtersForName: withFilters ? activeFilters : [],
    });
  }, [
    filterSelection,
    buildFilterParams,
    sortField,
    activeFilters,
    accounts,
    activeTenantId,
    formats,
    onClose,
    triggerVolumesExport,
  ]);

  const isExportDisabled = totalCount === undefined || isExporting('volumes') || formats.length === 0;

  return (
    <ExportModal
      isOpen={isOpen}
      onClose={onClose}
      title="Export"
      onExport={handleExport}
      isExportDisabled={isExportDisabled}
    >
      <ExportSection label="Format">
        <div className="col-span-2 grid grid-cols-2 gap-4">
          {FORMAT_OPTIONS.map((option) => (
            <SelectableOptionCard
              key={option.value}
              inputId={`vol-format-${option.value}`}
              checked={formats.includes(option.value)}
              label={option.label}
              description={option.description}
              onSelect={() => toggleFormat(option.value)}
            >
              <CheckboxOption
                id={`vol-format-${option.value}`}
                checked={formats.includes(option.value)}
                onChange={() => toggleFormat(option.value)}
              />
            </SelectableOptionCard>
          ))}
        </div>
      </ExportSection>

      <ExportSection label="Applied filters" borderBottom={false}>
        <RadioOptionList
          name="vol-filters"
          options={FILTER_OPTIONS}
          selected={filterSelection}
          onChange={setFilterSelection}
        />
      </ExportSection>
    </ExportModal>
  );
}
